(() => {
  if (customElements.get('shiftcam-lens-finder-dev')) return;

  class ShiftcamLensFinderDev extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.sectionId =
        this.dataset.sectionId || Math.random().toString(36).slice(2);

      this.csvUrl = (this.dataset.csvUrl || '').trim();

      this.mountIconColor =
        String(this.dataset.mountIconColor || 'white')
          .trim()
          .toLowerCase() === 'black'
          ? 'black'
          : 'white';

      this.mode = 'configure';

      this.state = {
        brand_key: '',
        phone_key: '',
        series_key: '',
        lens_key: ''
      };

      this.rows = [];

      this.productCache = new Map();
      this.productPrefetches = new Map();
      this.imagePrefetches = new Map();

      this.productRequestHandle = '';
      this.mountIconRequestKey = '';
      this.resultTransitionToken = 0;

      this.configPanel = this.querySelector(
        '[data-lens-finder-config]'
      );

      this.resultLoadingPanel = this.querySelector(
        '[data-lens-finder-result-loading]'
      );

      this.resultPanel = this.querySelector(
        '[data-lens-finder-result]'
      );

      this.statusPanel = this.querySelector(
        '[data-lens-finder-status]'
      );

      this.statusText = this.querySelector(
        '[data-lens-finder-status-text]'
      );

      this.lensLocked = this.querySelector(
        '[data-lens-finder-lens-locked]'
      );

      this.lensFields = this.querySelector(
        '[data-lens-finder-lens-fields]'
      );

      this.productImage = this.querySelector(
        '[data-result-product-image]'
      );

      this.productPlaceholder = this.querySelector(
        '[data-result-product-placeholder]'
      );

      this.mountIconWrap = this.querySelector(
        '[data-result-mount-icon-wrap]'
      );

      this.mountIcon = this.querySelector(
        '[data-result-mount-icon]'
      );

      this.shopLink = this.querySelector(
        '[data-result-shop]'
      );

      this.addEventListener(
        'change',
        this.onChange.bind(this),
        {
          signal: this.abortController.signal
        }
      );

      this.addEventListener(
        'click',
        this.onClick.bind(this),
        {
          signal: this.abortController.signal
        }
      );

      this.loadData();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    async loadData() {
      if (!this.csvUrl) {
        this.showStatus(
          'Lens Finder data source is not configured.'
        );

        this.setAttribute('aria-busy', 'false');

        console.warn(
          '[Lens Finder] No CSV URL was provided by the selected data-source metaobject.'
        );

        return;
      }

      this.showStatus('Loading Lens Finder…');

      try {
        const response = await fetch(this.csvUrl, {
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(
            `CSV request failed with HTTP ${response.status}.`
          );
        }

        const csvText = await response.text();
        const parsedRows = this.parseCSV(csvText);

        this.rows = this.normalizeRows(parsedRows);

        if (!this.rows.length) {
          throw new Error(
            'The CSV contains no active Lens Finder combinations.'
          );
        }

        this.validateRows(this.rows);

        this.hideStatus();

        if (this.configPanel) {
          this.configPanel.hidden = false;
        }

        this.setAttribute('aria-busy', 'false');

        this.render();
      } catch (error) {
        if (error?.name === 'AbortError') return;

        console.error('[Lens Finder]', error);

        this.showStatus(
          'Lens Finder data could not be loaded.'
        );

        this.setAttribute('aria-busy', 'false');
      }
    }

    parseCSV(text) {
      const source = String(text || '')
        .replace(/^\uFEFF/, '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');

      const rows = [];

      let row = [];
      let field = '';
      let inQuotes = false;

      for (
        let index = 0;
        index < source.length;
        index += 1
      ) {
        const character = source[index];

        if (inQuotes) {
          if (character === '"') {
            if (source[index + 1] === '"') {
              field += '"';
              index += 1;
            } else {
              inQuotes = false;
            }
          } else {
            field += character;
          }

          continue;
        }

        if (character === '"') {
          inQuotes = true;
        } else if (character === ',') {
          row.push(field);
          field = '';
        } else if (character === '\n') {
          row.push(field);
          rows.push(row);

          row = [];
          field = '';
        } else {
          field += character;
        }
      }

      if (field.length || row.length) {
        row.push(field);
        rows.push(row);
      }

      const nonBlankRows = rows.filter((cells) =>
        cells.some(
          (cell) =>
            String(cell || '').trim() !== ''
        )
      );

      if (!nonBlankRows.length) {
        return [];
      }

      const headers = nonBlankRows
        .shift()
        .map((header) =>
          String(header || '').trim()
        );

      const requiredHeaders = [
        'active',
        'phone_brand_key',
        'phone_brand_label',
        'phone_brand_sort',
        'phone_key',
        'phone_label',
        'phone_model_sort',
        'lens_series_key',
        'lens_series_label',
        'lens_series_sort',
        'lens_key',
        'lens_name',
        'lens_label',
        'lens_model_sort',
        'phone_camera',
        'mount_on_camera',
        'equivalent_focal_length_mm',
        'equivalent_optical_zoom_x',
        'camera_megapixel',
        'recommended_mount',
        'app_compatibility',
        'best_for',
        'how_to_step_1',
        'how_to_step_2',
        'how_to_step_3',
        'product_handle',
        'mount_icon_black_url',
        'mount_icon_white_url'
      ];

      const missingHeaders =
        requiredHeaders.filter(
          (header) => !headers.includes(header)
        );

      if (missingHeaders.length) {
        throw new Error(
          `CSV is missing required columns: ${missingHeaders.join(', ')}`
        );
      }

      return nonBlankRows.map((cells) => {
        const entry = {};

        headers.forEach((header, index) => {
          entry[header] =
            String(cells[index] ?? '').trim();
        });

        return entry;
      });
    }

    normalizeRows(rows) {
      return rows
        .filter((row) =>
          this.isActive(row.active)
        )
        .filter(
          (row) =>
            row.phone_key &&
            row.lens_key
        )
        .map((row) => ({
          ...row,

          phone_brand_sort:
            this.toSortNumber(
              row.phone_brand_sort
            ),

          phone_model_sort:
            this.toSortNumber(
              row.phone_model_sort
            ),

          lens_series_sort:
            this.toSortNumber(
              row.lens_series_sort
            ),

          lens_model_sort:
            this.toSortNumber(
              row.lens_model_sort
            ),

          phone_camera:
            String(
              row.phone_camera || ''
            ).toLowerCase(),

          best_for_items:
            this.splitCommaList(
              row.best_for
            ),

          how_to_use: [
            row.how_to_step_1,
            row.how_to_step_2,
            row.how_to_step_3
          ].filter(Boolean)
        }));
    }

    validateRows(rows) {
      const allowedPhoneCameras =
        new Set([
          'main',
          'tele'
        ]);

      const invalidPhoneCameraRows =
        rows.filter(
          (row) =>
            row.phone_camera &&
            !allowedPhoneCameras.has(
              row.phone_camera
            )
        );

      if (invalidPhoneCameraRows.length) {
        console.warn(
          '[Lens Finder] phone_camera should use only "main" or "tele".',
          invalidPhoneCameraRows
        );
      }

      const combinationKeys =
        new Set();

      const duplicateCombinations = [];

      rows.forEach((row) => {
        const key =
          `${row.phone_key}::${row.lens_key}`;

        if (combinationKeys.has(key)) {
          duplicateCombinations.push(key);
        }

        combinationKeys.add(key);
      });

      if (duplicateCombinations.length) {
        console.warn(
          '[Lens Finder] Duplicate active phone + lens combinations found:',
          duplicateCombinations
        );
      }
    }

    isActive(value) {
      return [
        'true',
        '1',
        'yes',
        'y'
      ].includes(
        String(value || '')
          .trim()
          .toLowerCase()
      );
    }

    toSortNumber(value) {
      const number = Number(value);

      return Number.isFinite(number)
        ? number
        : Number.MAX_SAFE_INTEGER;
    }

    splitCommaList(value) {
      return String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    showStatus(message) {
      if (this.statusText) {
        this.statusText.textContent =
          message;
      }

      if (this.statusPanel) {
        this.statusPanel.hidden = false;
      }

      if (this.configPanel) {
        this.configPanel.hidden = true;
      }

      if (this.resultLoadingPanel) {
        this.resultLoadingPanel.hidden = true;
      }

      if (this.resultPanel) {
        this.resultPanel.hidden = true;
      }
    }

    hideStatus() {
      if (this.statusPanel) {
        this.statusPanel.hidden = true;
      }
    }

    onChange(event) {
      const radioInput =
        event.target.closest(
          '[data-lens-finder-option]'
        );

      const selectInput =
        event.target.closest(
          '[data-choice-select]'
        );

      if (!radioInput && !selectInput) {
        return;
      }

      if (
        radioInput &&
        !radioInput.checked
      ) {
        return;
      }

      const input =
        radioInput || selectInput;

      const choice =
        input.dataset.choice;

      const value =
        input.value;

      if (!value) {
        return;
      }

      if (choice === 'brand') {
        this.resultTransitionToken += 1;

        this.state.brand_key = value;
        this.state.phone_key = '';
        this.state.series_key = '';
        this.state.lens_key = '';
      }

      if (choice === 'phone') {
        this.resultTransitionToken += 1;

        this.state.phone_key = value;

        const validSeries =
          this.getSeriesForPhone(value);

        const seriesStillValid =
          validSeries.some(
            (series) =>
              series.key ===
              this.state.series_key
          );

        if (!seriesStillValid) {
          this.state.series_key = '';
        }

        this.state.lens_key = '';
      }

      if (choice === 'series') {
        this.resultTransitionToken += 1;

        this.state.series_key = value;
        this.state.lens_key = '';
      }

      if (choice === 'lens') {
        this.state.lens_key = value;

        const result =
          this.getResult();

        this.render();

        if (result) {
          this.collapseResultDisclosures();
          this.startResultTransition(result);
        }

        return;
      }

      this.resultTransitionToken += 1;
      this.productRequestHandle = '';
      this.mountIconRequestKey = '';

      this.mode = 'configure';

      this.setAttribute(
        'aria-busy',
        'false'
      );

      this.render();
    }

    onClick(event) {
      const editButton =
        event.target.closest(
          '[data-lens-finder-edit]'
        );

      if (!editButton) {
        return;
      }

      this.resultTransitionToken += 1;
      this.productRequestHandle = '';
      this.mountIconRequestKey = '';

      this.mode = 'configure';

      this.setAttribute(
        'aria-busy',
        'false'
      );

      this.render();

      requestAnimationFrame(() => {
        const selectedLens =
          this.querySelector(
            '[data-choice-options="lens"] input:checked'
          );

        const selectedSeries =
          this.querySelector(
            '[data-choice-options="series"] input:checked'
          );

        const selectedPhone =
          this.querySelector(
            '[data-choice-select="phone"]'
          );

        const firstBrand =
          this.querySelector(
            '[data-choice-options="brand"] input'
          );

        (
          selectedLens ||
          selectedSeries ||
          selectedPhone ||
          firstBrand
        )?.focus();
      });
    }

    collapseResultDisclosures() {
      this.querySelectorAll(
        '[data-result-disclosure]'
      ).forEach((details) => {
        details.open = false;

        details.setAttribute(
          'aria-expanded',
          'false'
        );
      });
    }

    getBrands() {
      return this.uniqueSorted(
        this.rows,
        'phone_brand_key',
        'phone_brand_sort',
        (row) => ({
          key: row.phone_brand_key,
          label: row.phone_brand_label
        })
      );
    }

    getPhonesForBrand(brandKey) {
      return this.uniqueSorted(
        this.rows.filter(
          (row) =>
            row.phone_brand_key ===
            brandKey
        ),

        'phone_key',
        'phone_model_sort',

        (row) => ({
          key: row.phone_key,
          label: row.phone_label
        })
      );
    }

    getSeriesForPhone(phoneKey) {
      return this.uniqueSorted(
        this.rows.filter(
          (row) =>
            row.phone_key ===
            phoneKey
        ),

        'lens_series_key',
        'lens_series_sort',

        (row) => ({
          key: row.lens_series_key,
          label: row.lens_series_label
        })
      );
    }

    getLensesForPhoneAndSeries(
      phoneKey,
      seriesKey
    ) {
      return this.uniqueSorted(
        this.rows.filter(
          (row) =>
            row.phone_key === phoneKey &&
            row.lens_series_key ===
              seriesKey
        ),

        'lens_key',
        'lens_model_sort',

        (row) => ({
          key: row.lens_key,

          label:
            row.lens_name ||
            row.lens_label,

          fullLabel:
            row.lens_label,

          productHandle:
            row.product_handle,

          mountIconBlackUrl:
            row.mount_icon_black_url,

          mountIconWhiteUrl:
            row.mount_icon_white_url
        })
      );
    }

    uniqueSorted(
      rows,
      keyField,
      sortField,
      mapRow
    ) {
      const byKey = new Map();

      rows.forEach((row) => {
        if (
          !row[keyField] ||
          byKey.has(row[keyField])
        ) {
          return;
        }

        byKey.set(
          row[keyField],
          row
        );
      });

      return Array
        .from(byKey.values())
        .sort((a, b) => {
          const sortDifference =
            a[sortField] -
            b[sortField];

          if (sortDifference !== 0) {
            return sortDifference;
          }

          return String(
            mapRow(a).label || ''
          ).localeCompare(
            String(
              mapRow(b).label || ''
            )
          );
        })
        .map(mapRow);
    }

    getResult() {
      if (
        !this.state.phone_key ||
        !this.state.lens_key
      ) {
        return null;
      }

      return (
        this.rows.find(
          (row) =>
            row.phone_key ===
              this.state.phone_key &&
            row.lens_key ===
              this.state.lens_key
        ) || null
      );
    }

    render() {
      this.renderChoice(
        'brand',
        this.getBrands(),
        this.state.brand_key,
        false
      );

      const phones =
        this.state.brand_key
          ? this.getPhonesForBrand(
              this.state.brand_key
            )
          : [];

      this.renderChoice(
        'phone',
        phones,
        this.state.phone_key,
        !this.state.brand_key
      );

      const hasPhone =
        Boolean(
          this.state.phone_key
        );

      if (this.lensLocked) {
        this.lensLocked.hidden =
          hasPhone;
      }

      if (this.lensFields) {
        this.lensFields.hidden =
          !hasPhone;
      }

      const series =
        hasPhone
          ? this.getSeriesForPhone(
              this.state.phone_key
            )
          : [];

      this.renderChoice(
        'series',
        series,
        this.state.series_key,
        !hasPhone
      );

      const lenses =
        hasPhone &&
        this.state.series_key
          ? this.getLensesForPhoneAndSeries(
              this.state.phone_key,
              this.state.series_key
            )
          : [];

      this.renderChoice(
        'lens',
        lenses,
        this.state.lens_key,
        !this.state.series_key
      );

      if (lenses.length) {
        this.prefetchProducts(lenses);
        this.prefetchMountIcons(lenses);
      }

      this.renderMode();
    }

    renderChoice(
      choice,
      items,
      selectedKey,
      disabled
    ) {
      if (choice === 'phone') {
        this.renderPhoneSelect(
          items,
          selectedKey,
          disabled
        );

        return;
      }

      const fieldset =
        this.querySelector(
          `[data-choice-field="${choice}"]`
        );

      const list =
        this.querySelector(
          `[data-choice-options="${choice}"]`
        );

      const prompt =
        this.querySelector(
          `[data-choice-prompt="${choice}"]`
        );

      if (!fieldset || !list) {
        return;
      }

      fieldset.disabled =
        Boolean(disabled);

      list.replaceChildren();

      items.forEach(
        (item, index) => {
          const listItem =
            document.createElement('li');

          const input =
            document.createElement('input');

          const label =
            document.createElement('label');

          const inputId =
            `LensFinderV8-${this.sectionId}-${choice}-${this.slugify(item.key)}-${index}`;

          input.type = 'radio';
          input.className = 'sr-only';
          input.id = inputId;

          input.name =
            `LensFinderV8-${this.sectionId}-${choice}`;

          input.value = item.key;

          input.checked =
            item.key === selectedKey;

          input.dataset.lensFinderOption =
            '';

          input.dataset.choice =
            choice;

          label.className =
            'label-swatch text-sm font-medium cursor-pointer relative';

          label.htmlFor =
            inputId;

          label.textContent =
            item.label;

          listItem.append(
            input,
            label
          );

          list.append(
            listItem
          );
        }
      );

      if (prompt) {
        prompt.hidden =
          !disabled;
      }
    }

    renderPhoneSelect(
      items,
      selectedKey,
      disabled
    ) {
      const fieldset =
        this.querySelector(
          '[data-choice-field="phone"]'
        );

      const select =
        this.querySelector(
          '[data-choice-select="phone"]'
        );

      const prompt =
        this.querySelector(
          '[data-choice-prompt="phone"]'
        );

      if (!fieldset || !select) {
        return;
      }

      fieldset.disabled =
        Boolean(disabled);

      select.disabled =
        Boolean(disabled);

      select.replaceChildren();

      const placeholder =
        document.createElement(
          'option'
        );

      placeholder.value = '';

      placeholder.textContent =
        disabled
          ? 'Choose phone brand first'
          : 'Choose phone model';

      placeholder.disabled = true;

      placeholder.selected =
        !selectedKey;

      select.append(
        placeholder
      );

      items.forEach((item) => {
        const option =
          document.createElement(
            'option'
          );

        option.value =
          item.key;

        option.textContent =
          item.label;

        option.selected =
          item.key ===
          selectedKey;

        select.append(
          option
        );
      });

      if (selectedKey) {
        select.value =
          selectedKey;
      }

      if (prompt) {
        prompt.hidden =
          !disabled;
      }
    }

    renderMode() {
      const result =
        this.getResult();

      const hasResult =
        Boolean(result);

      if (
        (
          this.mode === 'result' ||
          this.mode === 'loading'
        ) &&
        !hasResult
      ) {
        this.mode =
          'configure';
      }

      this.dataset.mode =
        this.mode;

      const showLoading =
        this.mode === 'loading' &&
        hasResult;

      const showResult =
        this.mode === 'result' &&
        hasResult;

      const showConfig =
        !showLoading &&
        !showResult;

      if (this.configPanel) {
        this.configPanel.hidden =
          !showConfig;
      }

      if (this.resultLoadingPanel) {
        this.resultLoadingPanel.hidden =
          !showLoading;
      }

      if (this.resultPanel) {
        this.resultPanel.hidden =
          !showResult;
      }
    }

    populateResult(result) {
      if (!result) return;

      const focalNumber =
        this.formatNumber(
          result.equivalent_focal_length_mm
        );

      const focalLength =
        focalNumber
          ? `${focalNumber}mm`
          : '';

      const zoomValue =
        this.formatZoom(
          result.equivalent_optical_zoom_x
        );

      const megapixel =
        this.formatMegapixel(
          result.camera_megapixel
        );

      this.setTextAll(
        '[data-result-phone]',
        result.phone_label || ''
      );

      this.setTextAll(
        '[data-result-lens]',
        result.lens_label || ''
      );

      this.setTextAll(
        '[data-result-focal]',
        focalLength
      );

      this.setTextAll(
        '[data-result-zoom]',
        zoomValue
      );

      this.setTextAll(
        '[data-result-megapixel]',
        megapixel
      );

      this.setTextAll(
        '[data-result-camera]',
        result.mount_on_camera || ''
      );

      this.setTextAll(
        '[data-result-mount]',
        result.recommended_mount || ''
      );

      this.setTextAll(
        '[data-result-app]',
        result.app_compatibility || ''
      );

      this.setTextAll(
        '[data-result-helper]',
        focalLength
          ? `A similar field of view to using a ${focalLength} lens on a full-frame DSLR.`
          : ''
      );

      this.renderBestFor(
        result.best_for_items || []
      );

      this.renderSteps(
        (
          result.how_to_use || []
        ).slice(0, 3)
      );
    }

    async startResultTransition(
      result
    ) {
      const transitionToken =
        ++this.resultTransitionToken;

      const minimumLoadingTime =
        450;

      const maximumLoadingTime =
        1800;

      this.populateResult(
        result
      );

      this.resetProductImage(
        result.lens_label
      );

      this.resetMountIcon();

      this.mode = 'loading';

      this.setAttribute(
        'aria-busy',
        'true'
      );

      this.renderMode();

      this.scrollResultIntoViewOnMobile();

      const productReadyPromise =
        this.loadProduct(
          result.product_handle,
          result.lens_label
        ).catch((error) => {
          if (
            error?.name !==
            'AbortError'
          ) {
            console.warn(
              '[Lens Finder] Product preparation failed.',
              error
            );
          }

          return false;
        });

      const mountIconReadyPromise =
        this.loadMountIcon(
          result
        ).catch((error) => {
          if (
            error?.name !==
            'AbortError'
          ) {
            console.warn(
              '[Lens Finder] Mount icon preparation failed.',
              error
            );
          }

          return false;
        });

      const minimumDelay =
        this.delay(
          minimumLoadingTime
        );

      const boundedAssetWait =
        Promise.race([
          Promise.allSettled([
            productReadyPromise,
            mountIconReadyPromise
          ]),

          this.delay(
            maximumLoadingTime
          )
        ]);

      await Promise.all([
        minimumDelay,
        boundedAssetWait
      ]);

      if (
        transitionToken !==
        this.resultTransitionToken
      ) {
        return;
      }

      this.mode =
        'result';

      this.setAttribute(
        'aria-busy',
        'false'
      );

      this.renderMode();
    }

    scrollResultIntoViewOnMobile() {
      if (
        !window.matchMedia(
          '(max-width: 767px)'
        ).matches
      ) {
        return;
      }

      const target =
        this.querySelector(
          '.lens-finder-dev__stage'
        );

      if (!target) {
        return;
      }

      const prefersReducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      requestAnimationFrame(() => {
        target.scrollIntoView({
          behavior:
            prefersReducedMotion
              ? 'auto'
              : 'smooth',

          block: 'start'
        });
      });
    }

    delay(milliseconds) {
      return new Promise(
        (resolve) =>
          window.setTimeout(
            resolve,
            milliseconds
          )
      );
    }

    setTextAll(
      selector,
      value
    ) {
      this.querySelectorAll(
        selector
      ).forEach((element) => {
        element.textContent =
          value;
      });
    }

    renderBestFor(items) {
      this.querySelectorAll(
        '[data-result-best-for]'
      ).forEach((list) => {
        list.replaceChildren();

        items.forEach((item) => {
          const listItem =
            document.createElement('li');

          listItem.textContent =
            item;

          list.append(
            listItem
          );
        });
      });
    }

    renderSteps(steps) {
      this.querySelectorAll(
        '[data-result-steps]'
      ).forEach((list) => {
        list.replaceChildren();

        steps.forEach((step) => {
          const item =
            document.createElement('li');

          item.textContent =
            step;

          list.append(item);
        });
      });
    }

    prefetchProducts(lenses) {
      lenses.forEach((lens) => {
        const handle =
          String(
            lens.productHandle || ''
          ).trim();

        if (
          !handle ||
          this.productCache.has(handle) ||
          this.productPrefetches.has(handle)
        ) {
          return;
        }

        const prefetch =
          this.prefetchProduct(
            handle
          ).catch((error) => {
            if (
              error?.name !==
              'AbortError'
            ) {
              console.debug(
                `[Lens Finder] Product prefetch skipped for "${handle}".`,
                error
              );
            }

            return false;
          });

        this.productPrefetches.set(
          handle,
          prefetch
        );
      });
    }

    async prefetchProduct(handle) {
      const cleanHandle =
        String(handle || '').trim();

      if (!cleanHandle) {
        return false;
      }

      const root =
        window.Shopify?.routes?.root ||
        '/';

      const encodedHandle =
        encodeURIComponent(
          cleanHandle
        );

      const fallbackUrl =
        `${root}products/${encodedHandle}`;

      let product =
        this.productCache.get(
          cleanHandle
        ) || null;

      if (!product) {
        const response =
          await fetch(
            `${fallbackUrl}.js`,
            {
              signal:
                this.abortController.signal,

              headers: {
                Accept:
                  'application/json'
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            `Product prefetch failed with HTTP ${response.status}.`
          );
        }

        product =
          await response.json();

        this.productCache.set(
          cleanHandle,
          product
        );
      }

      const imageUrl =
        this.resolveProductImageUrl(
          product
        );

      if (imageUrl) {
        await this.preloadImage(
          imageUrl
        );
      }

      return true;
    }

    prefetchMountIcons(lenses) {
      lenses.forEach((lens) => {
        const urls =
          this.getMountIconUrls(
            lens
          );

        const url =
          urls[0] || urls[1];

        if (url) {
          this.preloadImage(url);
        }
      });
    }

    getMountIconUrls(source) {
      const blackUrl =
        this.extractImageUrl(
          source?.mount_icon_black_url ??
          source?.mountIconBlackUrl ??
          ''
        );

      const whiteUrl =
        this.extractImageUrl(
          source?.mount_icon_white_url ??
          source?.mountIconWhiteUrl ??
          ''
        );

      return this.mountIconColor ===
        'black'
        ? [
            blackUrl,
            whiteUrl
          ]
        : [
            whiteUrl,
            blackUrl
          ];
    }

    async loadMountIcon(result) {
      if (!result) {
        this.resetMountIcon();
        return false;
      }

      const requestKey =
        `${result.phone_key || ''}::${result.lens_key || ''}`;

      this.mountIconRequestKey =
        requestKey;

      const urls =
        this.getMountIconUrls(
          result
        );

      const preferredUrl =
        urls[0];

      const fallbackUrl =
        urls[1] &&
        urls[1] !== preferredUrl
          ? urls[1]
          : '';

      const alt =
        result.recommended_mount
          ? `${result.recommended_mount} mounting system`
          : 'Recommended mounting system';

      for (
        const url of [
          preferredUrl,
          fallbackUrl
        ]
      ) {
        if (!url) continue;

        const loaded =
          await this.preloadImage(
            url
          );

        if (
          this.mountIconRequestKey !==
          requestKey
        ) {
          return false;
        }

        if (loaded) {
          this.showMountIcon(
            url,
            alt,
            requestKey
          );

          return true;
        }
      }

      if (
        this.mountIconRequestKey ===
        requestKey
      ) {
        this.resetMountIcon();
      }

      return false;
    }

    resetMountIcon() {
      if (this.mountIcon) {
        this.mountIcon.hidden = true;
        this.mountIcon.removeAttribute(
          'src'
        );

        this.mountIcon.alt = '';
      }

      if (this.mountIconWrap) {
        this.mountIconWrap.hidden =
          true;
      }
    }

    showMountIcon(
      url,
      alt,
      expectedKey
    ) {
      if (
        !this.mountIcon ||
        !this.mountIconWrap ||
        !url
      ) {
        return;
      }

      if (
        expectedKey &&
        this.mountIconRequestKey !==
          expectedKey
      ) {
        return;
      }

      this.mountIcon.src =
        url;

      this.mountIcon.alt =
        alt ||
        'Recommended mounting system';

      this.mountIcon.hidden =
        false;

      this.mountIconWrap.hidden =
        false;
    }

    preloadImage(url) {
      const cleanUrl =
        this.extractImageUrl(url);

      if (!cleanUrl) {
        return Promise.resolve(
          false
        );
      }

      if (
        this.imagePrefetches.has(
          cleanUrl
        )
      ) {
        return this.imagePrefetches.get(
          cleanUrl
        );
      }

      const preload =
        new Promise((resolve) => {
          const image =
            new Image();

          image.decoding =
            'async';

          image.onload =
            () => resolve(true);

          image.onerror =
            () => resolve(false);

          image.src =
            cleanUrl;
        });

      this.imagePrefetches.set(
        cleanUrl,
        preload
      );

      return preload;
    }

    async loadProduct(
      handle,
      lensLabel
    ) {
      const cleanHandle =
        String(handle || '').trim();

      this.productRequestHandle =
        cleanHandle;

      this.resetProductImage(
        lensLabel
      );

      if (!this.shopLink) {
        return false;
      }

      if (!cleanHandle) {
        this.shopLink.hidden =
          true;

        this.shopLink.removeAttribute(
          'href'
        );

        return false;
      }

      const root =
        window.Shopify?.routes?.root ||
        '/';

      const encodedHandle =
        encodeURIComponent(
          cleanHandle
        );

      const fallbackUrl =
        `${root}products/${encodedHandle}`;

      this.shopLink.hidden =
        false;

      this.shopLink.href =
        fallbackUrl;

      const pendingPrefetch =
        this.productPrefetches.get(
          cleanHandle
        );

      if (pendingPrefetch) {
        try {
          await pendingPrefetch;
        } catch (_) {
          /* Normal product lookup below remains the source of truth. */
        }
      }

      let product =
        this.productCache.get(
          cleanHandle
        ) || null;

      let productImageUrl =
        '';

      if (!product) {
        try {
          const response =
            await fetch(
              `${fallbackUrl}.js`,
              {
                signal:
                  this.abortController.signal,

                headers: {
                  Accept:
                    'application/json'
                }
              }
            );

          if (!response.ok) {
            throw new Error(
              `Product lookup failed with HTTP ${response.status}.`
            );
          }

          product =
            await response.json();

          this.productCache.set(
            cleanHandle,
            product
          );
        } catch (error) {
          if (
            error?.name ===
            'AbortError'
          ) {
            return false;
          }

          console.warn(
            `[Lens Finder] Ajax Product API lookup failed for "${cleanHandle}". Falling back to product-page metadata.`,
            error
          );
        }
      }

      if (
        this.productRequestHandle !==
        cleanHandle
      ) {
        return false;
      }

      if (product?.url) {
        this.shopLink.href =
          this.absoluteStoreUrl(
            product.url
          );
      }

      productImageUrl =
        this.resolveProductImageUrl(
          product
        );

      if (!productImageUrl) {
        productImageUrl =
          await this.fetchProductPageImage(
            fallbackUrl,
            cleanHandle
          );
      }

      if (
        this.productRequestHandle !==
        cleanHandle
      ) {
        return false;
      }

      if (productImageUrl) {
        return this.preloadAndShowProductImage(
          productImageUrl,
          product?.title ||
            lensLabel ||
            'Selected lens',
          cleanHandle
        );
      }

      console.warn(
        `[Lens Finder] No product image could be resolved for handle "${cleanHandle}".`
      );

      return false;
    }

    resolveProductImageUrl(product) {
      if (!product) {
        return '';
      }

      const candidates = [
        product.featured_image,

        product.featuredImage,

        Array.isArray(
          product.images
        )
          ? product.images[0]
          : null,

        Array.isArray(
          product.media
        )
          ? product.media[0]
          : null,

        Array.isArray(
          product.variants
        )
          ? product.variants[0]
              ?.featured_image
          : null,

        Array.isArray(
          product.variants
        )
          ? product.variants[0]
              ?.image
          : null
      ];

      for (const candidate of candidates) {
        const url =
          this.extractImageUrl(
            candidate
          );

        if (url) {
          return url;
        }
      }

      return '';
    }

    async fetchProductPageImage(
      productUrl,
      expectedHandle
    ) {
      try {
        const response =
          await fetch(
            productUrl,
            {
              signal:
                this.abortController.signal,

              headers: {
                Accept:
                  'text/html'
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            `Product page lookup failed with HTTP ${response.status}.`
          );
        }

        const html =
          await response.text();

        if (
          this.productRequestHandle !==
          expectedHandle
        ) {
          return '';
        }

        const documentFragment =
          new DOMParser()
            .parseFromString(
              html,
              'text/html'
            );

        const selectors = [
          'meta[property="og:image:secure_url"]',
          'meta[property="og:image"]',
          'meta[name="twitter:image"]',
          'meta[name="twitter:image:src"]'
        ];

        for (
          const selector of selectors
        ) {
          const content =
            documentFragment
              .querySelector(selector)
              ?.getAttribute('content');

          const url =
            this.extractImageUrl(
              content
            );

          if (url) {
            return url;
          }
        }

        const jsonLdScripts =
          documentFragment
            .querySelectorAll(
              'script[type="application/ld+json"]'
            );

        for (
          const script of
          jsonLdScripts
        ) {
          try {
            const data =
              JSON.parse(
                script.textContent ||
                'null'
              );

            const image =
              this.findProductImageInJsonLd(
                data
              );

            const url =
              this.extractImageUrl(
                image
              );

            if (url) {
              return url;
            }
          } catch (_) {
            /* Ignore unrelated or malformed JSON-LD blocks. */
          }
        }
      } catch (error) {
        if (
          error?.name ===
          'AbortError'
        ) {
          return '';
        }

        console.warn(
          `[Lens Finder] Product-page image fallback failed for "${expectedHandle}".`,
          error
        );
      }

      return '';
    }

    findProductImageInJsonLd(data) {
      if (!data) {
        return '';
      }

      if (Array.isArray(data)) {
        for (const item of data) {
          const image =
            this.findProductImageInJsonLd(
              item
            );

          if (image) {
            return image;
          }
        }

        return '';
      }

      if (
        typeof data !==
        'object'
      ) {
        return '';
      }

      if (data['@graph']) {
        const image =
          this.findProductImageInJsonLd(
            data['@graph']
          );

        if (image) {
          return image;
        }
      }

      const type =
        Array.isArray(data['@type'])
          ? data['@type']
          : [data['@type']];

      if (
        type.includes('Product') &&
        data.image
      ) {
        return Array.isArray(
          data.image
        )
          ? data.image[0]
          : data.image;
      }

      return '';
    }

    extractImageUrl(candidate) {
      if (!candidate) {
        return '';
      }

      let value = '';

      if (
        typeof candidate ===
        'string'
      ) {
        value = candidate;
      } else if (
        typeof candidate ===
        'object'
      ) {
        value =
          candidate.url ||
          candidate.src ||
          candidate.originalSrc ||
          candidate.contentUrl ||
          '';

        if (
          !value &&
          candidate.preview?.image
        ) {
          value =
            candidate.preview.image.url ||
            candidate.preview.image.src ||
            '';
        }
      }

      value =
        String(value || '').trim();

      if (!value) {
        return '';
      }

      if (
        value.startsWith('//')
      ) {
        return `${window.location.protocol}${value}`;
      }

      if (
        value.startsWith('/')
      ) {
        return new URL(
          value,
          window.location.origin
        ).href;
      }

      return value;
    }

    absoluteStoreUrl(value) {
      const url =
        String(value || '').trim();

      if (!url) {
        return '';
      }

      if (
        /^https?:\/\//i.test(url)
      ) {
        return url;
      }

      return new URL(
        url,
        window.location.origin
      ).href;
    }

    resetProductImage(lensLabel) {
      if (this.productImage) {
        this.productImage.hidden =
          true;

        this.productImage.removeAttribute(
          'src'
        );

        this.productImage.alt =
          lensLabel ||
          'Selected lens';
      }

      if (this.productPlaceholder) {
        this.productPlaceholder.hidden =
          false;
      }
    }

    preloadAndShowProductImage(
      url,
      alt,
      expectedHandle
    ) {
      if (
        !this.productImage ||
        !url
      ) {
        return Promise.resolve(
          false
        );
      }

      return new Promise(
        (resolve) => {
          const preload =
            new Image();

          preload.decoding =
            'async';

          preload.onload = () => {
            if (
              this.productRequestHandle !==
              expectedHandle
            ) {
              resolve(false);
              return;
            }

            this.productImage.src =
              url;

            this.productImage.alt =
              alt ||
              'Selected lens';

            this.productImage.hidden =
              false;

            if (
              this.productPlaceholder
            ) {
              this.productPlaceholder.hidden =
                true;
            }

            resolve(true);
          };

          preload.onerror = () => {
            if (
              this.productRequestHandle ===
              expectedHandle
            ) {
              this.productImage.hidden =
                true;

              if (
                this.productPlaceholder
              ) {
                this.productPlaceholder.hidden =
                  false;
              }

              console.warn(
                `[Lens Finder] Browser could not load product image: ${url}`
              );
            }

            resolve(false);
          };

          preload.src =
            url;
        }
      );
    }

    formatNumber(value) {
      const number =
        Number.parseFloat(
          String(
            value ?? ''
          ).trim()
        );

      return Number.isFinite(number)
        ? String(number)
        : '';
    }

    formatZoom(value) {
      const formatted =
        this.formatNumber(value);

      return formatted
        ? `${formatted}×`
        : '';
    }

    formatMegapixel(value) {
      const text =
        String(value || '').trim();

      if (!text) {
        return '';
      }

      return /mp$/i.test(text)
        ? text
        : `${text}MP`;
    }

    slugify(value) {
      return String(value)
        .toLowerCase()
        .replace(
          /[^a-z0-9]+/g,
          '-'
        )
        .replace(
          /(^-|-$)/g,
          ''
        );
    }
  }

  customElements.define(
    'shiftcam-lens-finder-dev',
    ShiftcamLensFinderDev
  );
})();